import { computed } from "vue"
import { useStore } from "vuex"
import { setTopologyId } from "@/services/logger"
import { TOPOLOGY_USE_USER_PERMISSIONS } from "@/constants/topology"
import logger from "@/services/logger"
import { USER_PERMISSION_CHECK_COMPLETE } from "@/constants/events"
import {
  hasAuthoritativeAccessProfile,
  resolveBroadModuleAccessLevel,
  resolveModuleAccessLevel,
  resolvePackPermissions,
} from "@/security/moduleAccessAuthority"
import { useEditorActionPolicy } from "@/composables/useEditorActionPolicy"

// LLM:METADATA
// :topology: [Domain:Infrastructure | Role:Composable | Pattern:AuthorizationProvider]
// :relates-to: uses: "store/modules/auth.js", required-by: "Dashboard.vue", "Sidebar.vue", "useReports.js"
// :rationale: "Centralizes permission checks and prefers backend effective access profile over legacy frontend role maps."
// :contract: post: "Provides helper functions that interpret one effective permissions source for UI consumers."
// LLM:END

const MODULE_VIEW_LEVELS = ["view", "edit"]
const SERVICE_CAPABILITIES = [
  "services.compare_1c",
  "services.sales_1c",
  "services.prices",
  "services.refresh",
  "services.revaluation",
  "services.emails",
  "services.artist_change",
  "services.market_price",
  "services.tag_sync",
]

const normalizePositivePackIds = (packIds) =>
  [
    ...new Set(
      (Array.isArray(packIds) ? packIds : [packIds])
        .filter(
          (packId) =>
            typeof packId === "number" ||
            (typeof packId === "string" && packId.trim() !== "")
        )
        .map((packId) => Number(packId))
        .filter((packId) => Number.isFinite(packId) && packId > 0)
    ),
  ].sort((a, b) => a - b)

/**
 * Resolves the reports permission surface from the backend access profile.
 *
 * @precondition profile is either nullish or a backend-derived access profile object.
 * @postcondition Returns `true` for unrestricted report access, a deduplicated array for partial named-report grants, or `false` when reports are unavailable.
 * @mutates none
 */
const resolveReportPermissions = (profile) => {
  if (!profile) return false
  if (profile.is_superadmin) return true

  if (Array.isArray(profile.reports)) {
    const namedReports = profile.reports.filter(
      (reportName) => typeof reportName === "string" && reportName !== ""
    )
    return namedReports.length > 0 ? [...new Set(namedReports)] : false
  }

  if (!Array.isArray(profile.reports)) return false
  const namedReports = profile.reports.filter(
    (reportName) => typeof reportName === "string" && reportName !== ""
  )
  return namedReports.length > 0 ? [...new Set(namedReports)] : false
}

export function useUserPermissions() {
  setTopologyId(TOPOLOGY_USE_USER_PERMISSIONS)

  const store = useStore()
  const legacyPermissions = computed(() =>
    typeof store?.getters?.getUserRolePermissions === "function"
      ? store.getters.getUserRolePermissions()
      : {}
  )
  const accessProfile = computed(() =>
    typeof store?.getters?.getAccessProfile === "function"
      ? store.getters.getAccessProfile()
      : null
  )
  const userId = computed(() =>
    typeof store?.getters?.getUserId === "function"
      ? store.getters.getUserId()
      : null
  )
  const modules = computed(() =>
    typeof store?.getters?.getModules === "function"
      ? store.getters.getModules()
      : []
  )
  const menuModules = computed(() =>
    typeof store?.getters?.getMenuModules === "function"
      ? store.getters.getMenuModules()
      : []
  )
  const shouldLogPermissionChecks =
    process.env.NODE_ENV === "development" &&
    typeof window !== "undefined" &&
    window.localStorage?.getItem("debug_permissions") === "1"

  const logPermissionCheck = (message, data) => {
    if (!shouldLogPermissionChecks) return
    logger.debug(USER_PERMISSION_CHECK_COMPLETE, message, data)
  }

  const hasCapability = (capability) => {
    if (!capability) return false
    if (accessProfile.value?.is_superadmin) return true
    return accessProfile.value?.capabilities?.[capability] === true
  }

  const getModuleAccessLevel = (moduleId) => {
    return resolveModuleAccessLevel(
      accessProfile.value,
      moduleId,
      modules.value,
      legacyPermissions.value
    )
  }

  const canModuleAction = (moduleId, actionId, context = {}) => {
    const policy = useEditorActionPolicy({
      accessProfile: accessProfile.value,
      legacyPermissions: legacyPermissions.value,
      modules: modules.value,
      moduleId,
    })
    return policy.can(actionId, { ...context, moduleId })
  }

  const effectivePermissions = computed(() => {
    const profile = accessProfile.value
    if (!hasAuthoritativeAccessProfile(profile)) {
      return legacyPermissions.value || {}
    }

    const packScopes = profile.scopes?.packs || {}
    const broadModuleLevel = resolveBroadModuleAccessLevel(profile)
    const hasModuleScopes =
      Object.keys(profile?.scopes?.modules || {}).length > 0
    const hasPackScopes = Object.keys(packScopes).length > 0
    const hasAuthoritativeModuleInventory =
      Array.isArray(modules.value) && modules.value.length > 0

    let allowedModuleNames = false
    if (
      hasAuthoritativeModuleInventory &&
      (profile?.is_superadmin ||
        (!hasModuleScopes &&
          !hasPackScopes &&
          MODULE_VIEW_LEVELS.includes(broadModuleLevel)))
    ) {
      allowedModuleNames = true
    } else {
      const names = new Set()
      ;(modules.value || []).forEach((moduleRow) => {
        const moduleId = Number(moduleRow.module_id ?? moduleRow.id ?? 0)
        if (!Number.isFinite(moduleId) || moduleId <= 0) return

        const level = resolveModuleAccessLevel(
          profile,
          moduleId,
          modules.value,
          legacyPermissions.value
        )
        if (MODULE_VIEW_LEVELS.includes(level) && moduleRow.module_name) {
          names.add(moduleRow.module_name)
        }
      })
      allowedModuleNames = names.size > 0 ? [...names] : false
    }

    const reportPermissions = resolveReportPermissions(profile)
    const normalizedPackPermissions = normalizePositivePackIds(
      resolvePackPermissions(packScopes)
    )
    const unrestrictedPacks =
      hasAuthoritativeModuleInventory &&
      (profile?.is_superadmin ||
        (!hasModuleScopes &&
          !hasPackScopes &&
          MODULE_VIEW_LEVELS.includes(broadModuleLevel)))
    const moduleToPack = profile?.module_to_pack
    const moduleDerivedPackPermissions = unrestrictedPacks
      ? undefined
      : normalizePositivePackIds(
          (modules.value || [])
            .filter((moduleRow) => {
              const moduleId = Number(moduleRow.module_id ?? moduleRow.id ?? 0)
              if (!Number.isFinite(moduleId) || moduleId <= 0) return false
              return MODULE_VIEW_LEVELS.includes(getModuleAccessLevel(moduleId))
            })
            .flatMap((moduleRow) => {
              const moduleId = Number(moduleRow.module_id ?? moduleRow.id ?? 0)
              const packIds = moduleRow.module_pack_ids ?? moduleRow.pack_ids
              if (Array.isArray(packIds) && packIds.length > 0) return packIds
              const single = moduleRow.module_pack_id ?? moduleRow.pack_id
              if (single != null) return [single]
              const mapped =
                moduleToPack?.[moduleId] ?? moduleToPack?.[String(moduleId)]
              return Array.isArray(mapped)
                ? mapped
                : mapped != null
                  ? [mapped]
                  : []
            })
            .concat(
              (menuModules.value || [])
                .filter((pack) => Array.isArray(pack.moreItems))
                .flatMap((pack) =>
                  (pack.moreItems || [])
                    .filter((item) => {
                      const moduleId = Number(
                        item.module_id ?? item.id ?? item.url ?? 0
                      )
                      if (!Number.isFinite(moduleId) || moduleId <= 0)
                        return false
                      return MODULE_VIEW_LEVELS.includes(
                        getModuleAccessLevel(moduleId)
                      )
                    })
                    .map(() => pack.pack_id)
                )
            )
            .filter((id) => id != null)
        )

    return {
      access: hasCapability("roles.manage"),
      modules: allowedModuleNames,
      packs: unrestrictedPacks
        ? undefined
        : hasPackScopes
          ? normalizePositivePackIds([
              ...normalizedPackPermissions,
              ...moduleDerivedPackPermissions,
            ])
          : moduleDerivedPackPermissions,
      reports: reportPermissions,
      users: hasCapability("users.view"),
      users_export: hasCapability("users.export"),
      orders: hasCapability("orders.view"),
      cashbox: hasCapability("dashboard.cashbox"),
      promocode_messages: hasCapability("dashboard.promocode_messages"),
      // Shop visits uses the same backend dashboard.cashbox capability as its read/write endpoints.
      shop_visits: hasCapability("dashboard.cashbox"),
      oracle: hasCapability("dashboard.oracle"),
      oracle_reports: hasCapability("dashboard.oracle_reports"),
      pages: hasCapability("pages.view"),
      services: SERVICE_CAPABILITIES.some((capability) =>
        hasCapability(capability)
      ),
      carts: hasCapability("carts.view"),
      seo: hasCapability("seo.view"),
      options: hasCapability("system.options.view"),
      shortUrl: hasCapability("dashboard.short_url"),
    }
  })

  const permissions = computed(() => effectivePermissions.value)

  const isBlockPermitted = (block) => {
    const result =
      permissions?.value?.[block] !== undefined &&
      permissions?.value?.[block] !== false

    logPermissionCheck("Block permission checked", {
      block,
      result,
      hasPermissions: !!permissions?.value,
    })

    return result
  }

  const isModulePermitted = (moduleName) => {
    const modulePermissions = permissions?.value?.modules
    let result = false

    if (modulePermissions === undefined) result = false
    else if (modulePermissions === true) result = true
    else if (Array.isArray(modulePermissions))
      result = modulePermissions.includes(moduleName)
    else result = false

    logPermissionCheck("Module permission checked", {
      moduleName,
      result,
      modulesType: typeof modulePermissions,
      modulesValue: modulePermissions,
    })

    return result
  }

  const isPackPermitted = (packId) => {
    const packPermissions = permissions?.value?.packs
    const normalizedPackId = Number(packId)
    if (!Number.isFinite(normalizedPackId) || normalizedPackId <= 0) {
      return false
    }
    // An explicit pack scope list is self-authoritative: the pack scope itself
    // owns pack visibility and does not require the module inventory to load.
    if (Array.isArray(packPermissions)) {
      return (
        packPermissions.includes(normalizedPackId) ||
        packPermissions.includes(String(normalizedPackId))
      )
    }
    if (
      hasAuthoritativeAccessProfile(accessProfile.value) &&
      (!Array.isArray(modules.value) || modules.value.length === 0)
    ) {
      return false
    }
    if (packPermissions === undefined) return true
    return false
  }

  const isReportPermitted = (reportName) => {
    const reports = permissions?.value?.reports
    let result = false

    if (reports === undefined || reports === false) result = false
    else if (reports === true) result = true
    else if (Array.isArray(reports)) result = reports.includes(reportName)
    else result = false

    logPermissionCheck("Report permission checked", {
      reportName,
      result,
      reportsType: typeof reports,
      reportsValue: reports,
    })

    return result
  }

  const isServiceItemPermittedForUser = (serviceKey) => {
    const normalizedServiceKey = String(serviceKey || "").trim()
    const capability = normalizedServiceKey.startsWith("services.")
      ? normalizedServiceKey
      : `services.${normalizedServiceKey}`
    const result = hasCapability(capability)

    logPermissionCheck("Service item permission checked", {
      capability,
      result,
    })

    return result
  }

  const canExportUsers = computed(() => {
    return permissions?.value?.users_export === true
  })

  const canEditUsers = computed(() => hasCapability("users.edit"))
  const canImpersonateUsers = computed(() => hasCapability("users.impersonate"))
  const canCreateCatalogItems = computed(() => hasCapability("catalog.create"))
  const canDeleteCatalogItems = computed(() => hasCapability("catalog.delete"))
  const canPublishCatalogItems = computed(() =>
    hasCapability("catalog.publish")
  )
  const canExportCatalogItems = computed(() =>
    hasCapability("catalog.export_excel")
  )
  const canManageAccess = computed(() => hasCapability("roles.manage"))
  const canViewModule = (moduleId) =>
    MODULE_VIEW_LEVELS.includes(getModuleAccessLevel(moduleId))
  const canEditModule = (moduleId) => getModuleAccessLevel(moduleId) === "edit"

  return {
    permissions,
    accessProfile,
    userId,
    isBlockPermitted,
    isModulePermitted,
    isPackPermitted,
    isReportPermitted,
    isServiceItemPermittedForUser,
    canExportUsers,
    canEditUsers,
    canImpersonateUsers,
    hasCapability,
    canManageAccess,
    getModuleAccessLevel,
    canModuleAction,
    canViewModule,
    canEditModule,
    canCreateCatalogItems,
    canDeleteCatalogItems,
    canPublishCatalogItems,
    canExportCatalogItems,
  }
}
